import { useCallback, useMemo, useState } from "react";
import type { CustomTableColumn, OpenDropdownState, TableFilterState } from "../types";
import { getColumnValue, getLooseDataIndex, getLooseColumnKey, toValueArray } from "../utils/columnUtils";
import { CampaignStatusPhase, CampaignType } from "@/api/campaign";
import { countryOptions } from "../../../utils/formatters";
import { getColumnBehavior } from "../utils/tableHelpers";
import type { LooseColumn } from "../types";

type UseTableFiltersParams<T extends object> = {
	baseColumns: CustomTableColumn<T>[];
	dataSource?: T[];
	externalFilterState?: TableFilterState;
	onFilterStateChange?: (next: TableFilterState) => void;
};

export function useTableFilters<T extends object>(params: UseTableFiltersParams<T>) {
	const { baseColumns, dataSource, externalFilterState, onFilterStateChange } = params;
	const [openDropdown, setOpenDropdown] = useState<OpenDropdownState>(null);
	const [internalFilterState, setInternalFilterState] = useState<TableFilterState>({});
	const [filterSearch, setFilterSearch] = useState<Record<string, string>>({});

	const filterState = externalFilterState ?? internalFilterState;

	const updateFilterState = useCallback(
		(updater: (prev: TableFilterState) => TableFilterState) => {
			const next = updater(filterState);
			if (onFilterStateChange) {
				onFilterStateChange(next);
				return;
			}
			setInternalFilterState(next);
		},
		[filterState, onFilterStateChange],
	);

	const filterOptionsByColumn = useMemo(() => {
		const options: Record<string, string[]> = {};
		const rows = (dataSource || []) as T[];

		baseColumns.forEach((col) => {
			const looseCol = col as LooseColumn<T>;
			const { enableFilter } = getColumnBehavior(col);
			const colKey = getLooseColumnKey(looseCol);
			if (!enableFilter || !colKey || typeof col.title !== "string" || col.title.toLowerCase() === "action") {
				return;
			}

			const values = new Set<string>();
			rows.forEach((row) => {
				const rawValue = getColumnValue(row, getLooseDataIndex(looseCol));
				toValueArray(rawValue).forEach((v) => values.add(v));
			});

			let arr = Array.from(values);

			// If this column represents markets, use the canonical list from countryOptions
			if (colKey === "markets") {
				arr = countryOptions.map((c) => c.value);
			}
			if (colKey === "statusPhase") {
				const phaseOrder = Object.values(CampaignStatusPhase).map((v) => String(v).toLowerCase());
				arr.sort((a, b) => {
					const ia = phaseOrder.findIndex((v) => v === String(a).toLowerCase());
					const ib = phaseOrder.findIndex((v) => v === String(b).toLowerCase());
					if (ia === -1 && ib === -1) return String(a).localeCompare(String(b));
					if (ia === -1) return 1;
					if (ib === -1) return -1;
					return ia - ib;
				});
			} else if (colKey === "type") {
				const typeOrder = Object.values(CampaignType).map((v) => String(v).toLowerCase());
				arr.sort((a, b) => {
					const ia = typeOrder.findIndex((v) => v === String(a).toLowerCase());
					const ib = typeOrder.findIndex((v) => v === String(b).toLowerCase());
					if (ia === -1 && ib === -1) return String(a).localeCompare(String(b));
					if (ia === -1) return 1;
					if (ib === -1) return -1;
					return ia - ib;
				});
			} else {
				arr.sort((a, b) => a.localeCompare(b));
			}

			options[colKey] = arr;
		});

		return options;
	}, [baseColumns, dataSource]);

	const toggleFilterValue = useCallback(
		(columnKey: string, value: string) => {
			updateFilterState((prev) => {
				const current = prev[columnKey] || [];
				const exists = current.includes(value);
				const nextValues = exists ? current.filter((v) => v !== value) : [...current, value];
				return { ...prev, [columnKey]: nextValues };
			});
		},
		[updateFilterState],
	);

	const handleFilterSearchChange = useCallback((columnKey: string, value: string) => {
		setFilterSearch((prev) => ({ ...prev, [columnKey]: value }));
	}, []);

	return {
		filterState,
		openDropdown,
		setOpenDropdown,
		filterSearch,
		filterOptionsByColumn,
		toggleFilterValue,
		handleFilterSearchChange,
	};
}
